// Animations d'une espèce : une planche téléversée à la place de son image, et pour chaque animation les images
// choisies dans cette planche, jouées en boucle. Partagé par le serveur (vues envoyées au jeu), le jeu (rendu) et
// l'administration.
import type { AnimationFrame } from './moves.ts';

/**
 * Animations d'une espèce : `idle` au repos, de face (en combat face au joueur, en le suivant, posée sur la carte) ;
 * `back` de dos (en combat quand c'est le monstre du joueur, en suivant vers le haut) ; et leurs versions chromatiques
 * (sans elles, l'animation normale avec le filtre chromatique).
 */
export const SPRITE_ANIMATIONS = ['idle', 'back', 'shinyIdle', 'shinyBack'] as const;
export type SpriteAnimation = (typeof SPRITE_ANIMATIONS)[number];

/** Découpe de la planche : taille d'une image, vitesse, et images de chaque animation (vide : pas d'animation). */
export interface MonsterSpriteFrames extends Record<SpriteAnimation, AnimationFrame[]> {
  /** Taille de chaque image, en pixels de la planche. */
  width: number;
  height: number;
  /** Durée d'une image, en millisecondes. */
  frameMs: number;
}

export interface MonsterSprite extends MonsterSpriteFrames {
  /** Planche téléversée (URL de l'image). */
  sheet: string;
}

export const SPRITE_LIMITS = { maxFrames: 64, minMs: 40, maxMs: 2000, maxSide: 1024 } as const;
export const DEFAULT_SPRITE_FRAME_MS = 150;

/** Vu de face (repos) ou de dos. */
export type SpriteView = 'front' | 'back';

/**
 * Ce qu'il faut dessiner pour une vue : les images d'une animation (null : l'image fixe de l'espèce), si c'est
 * vraiment une vue de dos (sinon, la vue de face la remplace), et si le filtre chromatique s'applique (chromatique
 * sans animation dédiée).
 */
export function pickAnimation(sprite: MonsterSprite | null, view: SpriteView, shiny: boolean): { frames: AnimationFrame[] | null; back: boolean; filter: boolean } {
  const has = (key: SpriteAnimation) => sprite !== null && sprite[key].length > 0;
  if (view === 'back') {
    if (shiny && has('shinyBack')) return { frames: sprite!.shinyBack, back: true, filter: false };
    if (has('back')) return { frames: sprite!.back, back: true, filter: shiny };
  }
  if (shiny && has('shinyIdle')) return { frames: sprite!.shinyIdle, back: false, filter: false };
  return { frames: has('idle') ? sprite!.idle : null, back: false, filter: shiny };
}
